import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  ChevronLeft, ChevronRight, ArrowLeftRight, Sparkles,
  BarChart2, Clock, ShoppingCart, Info, Maximize2, X,
  TrendingUp, TrendingDown
} from 'lucide-react';
import styled, { keyframes } from 'styled-components';
import { getKmfcClient } from '../../lib/supabase';
import { usePermissions } from '../hooks/usePermissions';
import DatePickerField from '../components/pickers/DatePickerField';

// ─── Constants ────────────────────────────────────────────────────────────────
const PAID_STATUSES = ['nieuw', 'bereiding', 'klaar', 'onderweg', 'bezorgd', 'voltooid'];
const MIN_DATE = '2025-11-01';
const TRENDS_WEEKS_BACK = 10;

// ─── Formatters ───────────────────────────────────────────────────────────────
const euroFmt = new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' });
const fmtEuro = (v) => euroFmt.format(Number.isFinite(v) ? v : 0);
const fmtNum  = (v) => String(Math.round(Number.isFinite(v) ? v : 0));
const fmtDateKey = (d) => {
  const y = d.getFullYear(), m = String(d.getMonth()+1).padStart(2,'0'), day = String(d.getDate()).padStart(2,'0');
  return `${y}-${m}-${day}`;
};

const NL_MONTHS   = ['Januari','Februari','Maart','April','Mei','Juni','Juli','Augustus','September','Oktober','November','December'];
const NL_MONTHS_S = ['jan','feb','mrt','apr','mei','jun','jul','aug','sep','okt','nov','dec'];
const DAY_SHORT   = ['Ma','Di','Wo','Do','Vr','Za','Zo'];
const DAY_FULL    = ['Maandag','Dinsdag','Woensdag','Donderdag','Vrijdag','Zaterdag','Zondag'];

// ─── Amsterdam timezone ───────────────────────────────────────────────────────
const toAms = (date) => new Date(date.toLocaleString('en-US', { timeZone: 'Europe/Amsterdam' }));
const weekdayIdx = (date) => (toAms(date).getDay() + 6) % 7; // 0=Ma
const hourOf     = (date) => toAms(date).getHours();

// ─── ISO week helpers ─────────────────────────────────────────────────────────
const getMondayOfWeek = (week, year) => {
  const jan4 = new Date(year, 0, 4);
  const dow = (jan4.getDay() + 6) % 7;
  const mon = new Date(jan4); mon.setDate(jan4.getDate() - dow + (week - 1) * 7);
  return mon;
};
const getISOWeek = (d) => {
  const dd = new Date(d); dd.setHours(0,0,0,0); dd.setDate(dd.getDate() + 3 - ((dd.getDay()+6)%7));
  const w1 = new Date(dd.getFullYear(), 0, 4);
  return 1 + Math.round(((dd - w1) / 86400000 - 3 + ((w1.getDay()+6)%7)) / 7);
};
const getISOWeekYear = (d) => { const dd = new Date(d); dd.setDate(dd.getDate() + 3 - ((dd.getDay()+6)%7)); return dd.getFullYear(); };

// ─── Dutch holidays ───────────────────────────────────────────────────────────
function easterDate(year) {
  const a = year % 19, b = Math.floor(year/100), c = year % 100;
  const d = Math.floor(b/4), e = b%4, f = Math.floor((b+8)/25);
  const g = Math.floor((b-f+1)/3), h = (19*a+b-d-g+15)%30;
  const i = Math.floor(c/4), k = c%4, l = (32+2*e+2*i-h-k)%7;
  const m = Math.floor((a+11*h+22*l)/451);
  const month = Math.floor((h+l-7*m+114)/31);
  const day   = ((h+l-7*m+114)%31)+1;
  return new Date(year, month-1, day);
}
const _holidayCache = {};
function isHoliday(date) {
  const d = toAms(date), y = d.getFullYear();
  if (!_holidayCache[y]) {
    const s = new Set();
    [[1,1],[4,27],[5,5],[12,25],[12,26]].forEach(([m,dd]) => s.add(fmtDateKey(new Date(y,m-1,dd))));
    const e = easterDate(y);
    [-2,0,1,39,49,50].forEach(off => { const x=new Date(e); x.setDate(x.getDate()+off); s.add(fmtDateKey(x)); });
    s.add(fmtDateKey(new Date(y+1,0,1)));
    _holidayCache[y] = s;
  }
  return _holidayCache[y].has(fmtDateKey(d));
}

// ─── Data helpers ─────────────────────────────────────────────────────────────
const fetchGeneralStats = async (startDate, endDate) => {
  const client = getKmfcClient();
  if (!client) return null;
  const safeEnd = endDate || fmtDateKey(new Date());
  const toUTC    = (d, t) => new Date(`${d}T${t}:00`).toISOString();
  const toUTCEnd = (d, t) => { const dt = new Date(`${d}T${t}:00`); dt.setSeconds(59,999); return dt.toISOString(); };
  const isAll = !startDate;

  let q = client.from('orders').select('id,placed_at,status,is_test,total_cents,tip_cents,delivery_type,delivery_fee_cents,delivery_km');
  if (!isAll) q = q.gte('placed_at', toUTC(startDate,'00:00')).lte('placed_at', toUTCEnd(safeEnd,'23:59'));
  const { data: all, error } = await q.order('placed_at', { ascending: false });
  if (error) throw error;

  const orders = (all||[]).filter(o => !o.is_test && o.placed_at && PAID_STATUSES.includes(o.status));
  const totalRevCents  = orders.reduce((s,o) => s+(o.total_cents||0), 0);
  const delivFeeCents  = orders.reduce((s,o) => s+(o.delivery_fee_cents||0), 0);
  const delivOrders    = orders.filter(o => o.delivery_type==='delivery');
  const totalKm        = delivOrders.reduce((s,o) => s+(parseFloat(o.delivery_km)||0), 0);
  const avgKm          = delivOrders.length>0 ? totalKm/delivOrders.length : 0;
  const delivCount     = delivOrders.length;
  const pickCount      = orders.filter(o => o.delivery_type!=='delivery').length;
  const avgOrderCents  = orders.length > 0 ? Math.round(totalRevCents/orders.length) : 0;
  const totalTipCents  = orders.reduce((s,o) => s+(o.tip_cents||0), 0);

  const orderIds = orders.map(o => o.id);
  let upsellItemCount=0, upsellOrderCount=0;
  if (orderIds.length > 0) {
    const { data: items } = await client.from('order_items').select('order_id,qty,item_options').in('order_id', orderIds);
    if (items) {
      const upsellItems = items.filter(i => i.item_options && JSON.stringify(i.item_options).includes('"is_upsell":true'));
      upsellItemCount  = upsellItems.reduce((s,i) => s+(i.qty||1), 0);
      upsellOrderCount = new Set(upsellItems.map(i => i.order_id)).size;
    }
  }
  return { totalRevCents, delivFeeCents, avgKm, totalKm, totalTipCents, orderCount: orders.length, delivCount, pickCount, avgOrderCents, upsellItemCount, upsellOrderCount };
};

const computeTrendsData = (orders) => {
  const now = new Date();
  const parsed = orders
    .map(o => ({ date: new Date(o.placed_at), ...o }))
    .filter(o => !isHoliday(o.date));

  // Hourly grid
  const hourlyGrid = {}, ordersByDay = {};
  parsed.forEach(o => {
    const h = hourOf(o.date), d = weekdayIdx(o.date);
    hourlyGrid[`${d}_${h}`] = (hourlyGrid[`${d}_${h}`]||0)+1;
    ordersByDay[d] = (ordersByDay[d]||0)+1;
  });

  const usedHours = [...new Set(parsed.map(o => hourOf(o.date)))].sort((a,b)=>a-b);
  const visibleHours = usedHours.length>0
    ? Array.from({length: usedHours[usedHours.length-1]-usedHours[0]+1}, (_,i) => usedHours[0]+i)
    : [];

  // Weeks per day
  const todayWd = weekdayIdx(now);
  const weeksPerDay = Array.from({length:7},(_,d) => d<=todayWd ? TRENDS_WEEKS_BACK : TRENDS_WEEKS_BACK-1);

  const avgOrdersPerWeekday = Array.from({length:7},(_,d) =>
    (ordersByDay[d]||0) / Math.max(1, weeksPerDay[d])
  );
  const closedDays = new Set(Array.from({length:7},(_,d)=>d).filter(d=>!ordersByDay[d]));

  // Probability
  let todayOrderProbability=null, probExpectedRemaining=0, ordersTodayCount=0,
      probDampedFactor=1.0, probIsReliable=false, probExtreme=false;
  const probWeekdayName = DAY_FULL[todayWd];
  const probCurrentHour = hourOf(now);

  const isToday = (date) => {
    const a = toAms(date), b = toAms(now);
    return a.getFullYear()===b.getFullYear() && a.getMonth()===b.getMonth() && a.getDate()===b.getDate();
  };

  if (!closedDays.has(todayWd)) {
    const historicSameDay = parsed.filter(o => weekdayIdx(o.date)===todayWd && !isToday(o.date));
    const weeksOfData = Math.max(1, weeksPerDay[todayWd]);
    const ordersToday = parsed.filter(o => isToday(o.date)).length;

    let lambdaBasis = 0;
    for (let h = probCurrentHour; h <= 23; h++) {
      lambdaBasis += historicSameDay.filter(o => hourOf(o.date)===h).length / weeksOfData;
    }

    const beforeNow    = historicSameDay.filter(o => hourOf(o.date)<probCurrentHour).length;
    const avgBeforeNow = beforeNow / weeksOfData;
    const rawFactor    = avgBeforeNow>0 ? ordersToday/avgBeforeNow : (ordersToday>0 ? 1.5 : 1.0);
    const wO = Math.min(1.0, ordersToday/6.0), wW = Math.min(1.0, weeksOfData/12.0);
    let damped = Math.max(0.3, Math.min(2.5, 1.0+(rawFactor-1.0)*wO*wW));

    const lambda = lambdaBasis * damped;
    const p = Math.min(0.97, 1.0-Math.exp(-lambda));

    todayOrderProbability  = historicSameDay.length>=3 ? p : null;
    probExpectedRemaining  = Math.round(lambda*10)/10;
    ordersTodayCount       = ordersToday;
    probDampedFactor       = Math.round(damped*10)/10;
    probIsReliable         = weeksOfData>=8;
    probExtreme            = damped>2.0;
  }

  return { hourlyGrid, visibleHours, avgOrdersPerWeekday, closedDays,
    todayOrderProbability, probExpectedRemaining, probWeekdayName, probCurrentHour,
    ordersTodayCount, probDampedFactor, probIsReliable, probExtreme };
};

const fetchTrendsData = async () => {
  const client = getKmfcClient();
  if (!client) return null;
  const since = new Date(); since.setDate(since.getDate()-TRENDS_WEEKS_BACK*7); since.setHours(0,0,0,0);
  const { data: all, error } = await client.from('orders')
    .select('id,placed_at,status,is_test')
    .gte('placed_at', since.toISOString())
    .neq('status','unpaid');
  if (error) throw error;
  const orders = (all||[]).filter(o => !o.is_test && o.placed_at);
  return computeTrendsData(orders);
};

const fetchProductsData = async () => {
  const client = getKmfcClient();
  if (!client) return null;
  const since = new Date(); since.setDate(since.getDate()-TRENDS_WEEKS_BACK*7); since.setHours(0,0,0,0);

  const { data: orders, error: oErr } = await client.from('orders')
    .select('id,placed_at,status,is_test').gte('placed_at', since.toISOString());
  if (oErr) throw oErr;
  const validOrders = (orders||[]).filter(o => !o.is_test && o.placed_at && o.status?.toLowerCase()!=='unpaid');
  const orderIds = validOrders.map(o => o.id);
  if (!orderIds.length) return { productTrends: [] };

  const orderDateMap = {};
  validOrders.forEach(o => { orderDateMap[o.id] = new Date(o.placed_at); });

  const { data: items, error: iErr } = await client.from('order_items')
    .select('order_id,qty,name_snapshot').in('order_id', orderIds);
  if (iErr) throw iErr;

  const now = new Date();
  const fourWeeksAgo  = new Date(now); fourWeeksAgo.setDate(now.getDate()-28);
  const eightWeeksAgo = new Date(now); eightWeeksAgo.setDate(now.getDate()-56);

  const recent={}, previous={}, displayNames={};
  (items||[]).forEach(item => {
    const name = item.name_snapshot?.trim();
    if (!name) return;
    const key = name.toLowerCase();
    const date = orderDateMap[item.order_id];
    if (!date) return;
    if (date>=fourWeeksAgo) {
      recent[key] = (recent[key]||0)+(item.qty||1);
      displayNames[key] = name;
    } else if (date>=eightWeeksAgo) {
      previous[key] = (previous[key]||0)+(item.qty||1);
      if (!displayNames[key]) displayNames[key] = name;
    }
  });

  const toTitle = (s) => (s===s.toUpperCase()) ? s.charAt(0).toUpperCase()+s.slice(1).toLowerCase() : s;
  const allKeys = new Set([...Object.keys(recent),...Object.keys(previous)]);
  const productTrends = [...allKeys].map(key => {
    const recentQty=recent[key]||0, previousQty=previous[key]||0;
    const name=toTitle(displayNames[key]||key);
    const changeRatio = previousQty>0 ? (recentQty-previousQty)/previousQty : (recentQty>0?1.0:0);
    return {
      key, name, recentQty, previousQty,
      weeklyAvg: recentQty/4.0, changeRatio,
      isTrending:  previousQty>0 && changeRatio>0.25 && recentQty>=3,
      isDeclining: previousQty>=3 && changeRatio<-0.25 && recentQty<previousQty,
    };
  }).filter(t=>t.recentQty>=1||t.previousQty>=1).sort((a,b)=>b.recentQty-a.recentQty);

  return { productTrends };
};

// ─── Animations ───────────────────────────────────────────────────────────────
const fadeUp = keyframes`from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}`;
const pulse  = keyframes`from{opacity:0.04}to{opacity:0.09}`;

// ─── Styled components ────────────────────────────────────────────────────────
const Container = styled.div`
  height: 100vh;
  background: ${p=>p.theme.colors.background};
  overflow: hidden;
`;
const Content = styled.div`
  padding: 20px 20px 48px;
  height: calc(100vh - 52px);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  @media(max-width:768px){padding:14px 14px 48px}
`;
const Inner = styled.div`width:100%`;
const PageTitle = styled.h1`
  font-size:28px;font-weight:700;color:${p=>p.theme.colors.text};
  letter-spacing:-0.02em;margin:0 0 14px;
`;

// Tab bar
const TabBar = styled.div`
  display:flex;gap:4px;padding:5px;
  background:${p=>p.theme.mode==='dark'?'rgba(255,255,255,0.05)':'rgba(0,0,0,0.04)'};
  border:1px solid ${p=>p.theme.colors.border};
  border-radius:18px;margin-bottom:16px;
`;
const TabBtn = styled.button`
  flex:1;display:flex;align-items:center;justify-content:center;gap:5px;
  padding:8px 10px;border-radius:13px;border:none;cursor:pointer;
  font-family:inherit;font-size:12px;font-weight:600;white-space:nowrap;
  transition:all 0.2s;
  background:${p=>p.$active?(p.theme.mode==='dark'?'rgba(255,255,255,0.1)':'rgba(255,255,255,0.9)'):'transparent'};
  color:${p=>p.$active?p.theme.colors.text:p.theme.colors.muted};
  box-shadow:${p=>p.$active?'0 1px 4px rgba(0,0,0,0.12)':'none'};
  opacity:${p=>p.$active?1:0.55};
  &:hover{opacity:1}
`;

// Period bar
const PeriodBar = styled.div`
  display:flex;background:${p=>p.theme.colors.surface};
  border:1px solid ${p=>p.theme.colors.border};
  border-radius:14px;padding:4px;gap:2px;margin-bottom:12px;
  overflow-x:auto;scrollbar-width:none;
  &::-webkit-scrollbar{display:none}
`;
const PeriodBtn = styled.button`
  flex:1;padding:8px 10px;border-radius:10px;border:none;
  background:${p=>p.$active?p.theme.colors.background:'transparent'};
  color:${p=>p.$active?p.theme.colors.text:p.theme.colors.muted};
  font-size:13px;font-weight:${p=>p.$active?600:500};font-family:inherit;
  cursor:pointer;white-space:nowrap;flex-shrink:0;
  box-shadow:${p=>p.$active?'0 1px 4px rgba(0,0,0,0.1)':'none'};
  transition:all 0.15s;
  &:hover{color:${p=>p.theme.colors.text}}
`;

const Navigator = styled.div`
  display:flex;align-items:center;
  background:${p=>p.theme.colors.surface};border:1px solid ${p=>p.theme.colors.border};
  border-radius:14px;padding:4px 6px;margin-bottom:16px;gap:4px;
`;
const NavBtn = styled.button`
  width:36px;height:36px;border-radius:9px;border:none;
  background:${p=>p.theme.colors.background};color:${p=>p.theme.colors.muted};
  display:flex;align-items:center;justify-content:center;
  cursor:pointer;flex-shrink:0;transition:all 0.15s;
  &:hover:not(:disabled){color:${p=>p.theme.colors.text}}
  &:disabled{opacity:0.25;cursor:not-allowed}
`;
const NavLabel = styled.div`flex:1;text-align:center;font-size:15px;font-weight:600;color:${p=>p.theme.colors.text};letter-spacing:-0.01em`;
const NavSub   = styled.div`font-size:11px;color:${p=>p.theme.colors.muted};margin-top:1px`;

const Card = styled.div`
  background:${p=>p.theme.colors.surface};border:1px solid ${p=>p.theme.colors.border};
  border-radius:20px;overflow:hidden;
  animation:${fadeUp} 0.35s ease both;
  animation-delay:${p=>p.$delay||0}ms;
`;
const CardHeader = styled.div`display:flex;align-items:center;gap:10px;padding:16px 18px 14px`;
const IconBox = styled.div`
  width:30px;height:30px;border-radius:8px;flex-shrink:0;
  background:${p=>p.theme.mode==='dark'?'rgba(255,255,255,0.08)':'rgba(0,0,0,0.06)'};
  display:flex;align-items:center;justify-content:center;
  color:${p=>p.theme.colors.text};opacity:0.65;
`;
const CardTitle = styled.div`font-size:13px;font-weight:600;color:${p=>p.theme.colors.text}`;
const CardSub   = styled.div`font-size:11px;color:${p=>p.theme.colors.muted};margin-top:1px`;
const Divider   = styled.div`height:1px;background:${p=>p.theme.colors.border}`;
const VDivider  = styled.div`width:1px;background:${p=>p.theme.colors.border};margin:12px 0`;

const HeroBody    = styled.div`padding:18px 18px 20px`;
const HeroLabel   = styled.div`font-size:10px;font-weight:700;letter-spacing:0.1em;text-transform:uppercase;color:${p=>p.theme.colors.muted};margin-bottom:6px`;
const HeroValue   = styled.div`font-size:clamp(36px,5vw,52px);font-weight:700;color:${p=>p.theme.colors.text};letter-spacing:-0.03em;line-height:1;margin-bottom:6px`;
const HeroPeriod  = styled.div`font-size:13px;color:${p=>p.theme.colors.muted}`;

const TileRow  = styled.div`display:grid;grid-template-columns:1fr 1fr;gap:12px`;
const TileBody = styled.div`padding:16px 18px 18px`;
const TileLabel= styled.div`font-size:11px;font-weight:600;color:${p=>p.theme.colors.muted};margin-bottom:10px`;
const TileValue= styled.div`font-size:${p=>p.$large?'28px':'26px'};font-weight:700;color:${p=>p.theme.colors.text};letter-spacing:-0.02em;line-height:1`;
const TileCap  = styled.div`font-size:11px;color:${p=>p.theme.colors.muted};margin-top:3px`;

const SplitRow  = styled.div`display:flex`;
const SplitHalf = styled.div`flex:1;padding:16px 18px`;
const ProgTrack = styled.div`height:10px;border-radius:5px;background:${p=>p.theme.mode==='dark'?'rgba(255,255,255,0.08)':'rgba(0,0,0,0.06)'};overflow:hidden;display:flex;gap:3px;margin:0 18px 16px`;
const ProgFill  = styled.div`height:100%;border-radius:5px;background:${p=>p.theme.colors.text};width:${p=>p.$pct}%;transition:width 0.6s ease`;
const ProgRest  = styled.div`height:100%;border-radius:5px;flex:1;background:${p=>p.theme.mode==='dark'?'rgba(255,255,255,0.1)':'rgba(0,0,0,0.08)'}`;
const FootNote  = styled.div`font-size:11px;color:${p=>p.theme.colors.muted};padding:0 18px 14px;opacity:0.6`;

const Stack = styled.div`display:flex;flex-direction:column;gap:${p=>p.$gap||12}px`;

const Skel = styled.div`
  height:${p=>p.$h||20}px;border-radius:8px;
  background:${p=>p.theme.mode==='dark'?'rgba(255,255,255,0.06)':'rgba(0,0,0,0.05)'};
  animation:${pulse} 0.9s ease-in-out infinite alternate;
`;

// Heatmap
const HeatGrid = styled.div`display:flex;flex-direction:column;gap:2px`;
const HeatRow  = styled.div`display:flex;gap:2px;align-items:center`;
const HeatLabel= styled.div`font-size:8px;color:${p=>p.theme.colors.muted};width:26px;text-align:right;flex-shrink:0;opacity:0.7`;
const DayLabel = styled.div`font-size:9px;font-weight:600;color:${p=>p.theme.colors.muted};flex:1;text-align:center;opacity:0.6`;
const HeatCell = styled.div`
  flex:1;height:18px;border-radius:3px;
  background:${p=>p.$intensity===0
    ? (p.theme.mode==='dark'?'rgba(255,255,255,0.05)':'rgba(0,0,0,0.05)')
    : `rgba(255,149,0,${0.2+p.$intensity*0.75})`};
  transition:background 0.2s;
`;

// Bar chart (piekdagen)
const BarTrack = styled.div`
  flex:1;height:10px;border-radius:4px;
  background:${p=>p.theme.mode==='dark'?'rgba(255,255,255,0.08)':'rgba(0,0,0,0.06)'};
  overflow:hidden;position:relative;
`;
const BarFill = styled.div`
  height:100%;border-radius:4px;
  background:${p=>p.theme.mode==='dark'?'rgba(255,255,255,0.7)':'rgba(0,0,0,0.65)'};
  width:${p=>p.$pct}%;transition:width 0.5s ease;
`;

// Probability
const ProbValue = styled.span`font-size:42px;font-weight:700;color:${p=>p.theme.colors.text};letter-spacing:-0.03em`;
const ProbSub   = styled.span`font-size:14px;color:${p=>p.theme.colors.muted};margin-left:6px;vertical-align:baseline;position:relative;top:-4px`;
const ProbTrack = styled.div`
  height:10px;border-radius:6px;margin:12px 0 14px;
  background:${p=>p.theme.mode==='dark'?'rgba(255,255,255,0.08)':'rgba(0,0,0,0.06)'};
  overflow:hidden;
`;
const ProbFill = styled.div`
  height:100%;border-radius:6px;
  background:${p=>{const v=p.$val; return v<0.3?'rgba(255,59,48,0.6)':v<0.6?'rgba(255,149,0,0.7)':'rgba(52,199,89,0.7)'}};
  width:${p=>p.$pct}%;transition:width 0.6s ease;
`;

// Info expand
const InfoBtn = styled.button`
  display:flex;align-items:center;gap:6px;width:100%;border:none;background:none;
  cursor:pointer;padding:0;font-family:inherit;
`;
const InfoText  = styled.span`font-size:12px;font-weight:500;color:${p=>p.theme.colors.muted}`;
const InfoExpand= styled.div`font-size:11px;color:${p=>p.theme.colors.muted};opacity:0.7;margin-top:8px`;
const InfoNr    = styled.span`
  display:inline-flex;align-items:center;justify-content:center;
  width:16px;height:16px;border-radius:50%;flex-shrink:0;
  background:${p=>p.theme.mode==='dark'?'rgba(255,255,255,0.1)':'rgba(0,0,0,0.07)'};
  font-size:9px;font-weight:700;color:${p=>p.theme.colors.muted};margin-right:6px;
`;

// Product rows
const ProdRow = styled.div`display:flex;align-items:center;gap:12px;padding:10px 18px`;
const ProdName= styled.div`font-size:13px;font-weight:600;color:${p=>p.theme.colors.text};line-height:1.2;margin-bottom:4px`;
const ProdBar = styled.div`
  height:6px;border-radius:3px;
  background:${p=>p.theme.mode==='dark'?'rgba(255,255,255,0.08)':'rgba(0,0,0,0.06)'};
  overflow:hidden;
`;
const ProdBarFill = styled.div`
  height:100%;border-radius:3px;
  background:${p=>p.theme.mode==='dark'?'rgba(255,255,255,0.5)':'rgba(0,0,0,0.45)'};
  width:${p=>p.$pct}%;
`;
const ProdWeekly= styled.div`text-align:right;min-width:54px`;
const ProdWNum  = styled.div`font-size:16px;font-weight:700;color:${p=>p.theme.colors.text};letter-spacing:-0.02em;line-height:1`;
const ProdWUnit = styled.span`font-size:10px;font-weight:500;color:${p=>p.theme.colors.muted};margin-left:1px`;
const ProdDelta = styled.div`
  display:inline-flex;align-items:center;gap:2px;margin-top:2px;
  font-size:10px;font-weight:600;
  color:${p=>p.$up?'rgba(52,199,89,0.85)':'rgba(255,59,48,0.75)'};
`;

// Trenders
const TrendBadge = styled.div`
  display:inline-flex;align-items:center;gap:3px;
  padding:3px 8px;border-radius:20px;font-size:12px;font-weight:700;
  background:${p=>p.$up?'rgba(52,199,89,0.09)':'rgba(255,59,48,0.07)'};
  color:${p=>p.$up?'rgba(52,199,89,0.9)':'rgba(255,59,48,0.8)'};
`;

// Heatmap modal
const ModalOverlay = styled.div`
  position:fixed;inset:0;z-index:9999;
  background:rgba(0,0,0,0.5);display:flex;flex-direction:column;
`;
const ModalInner = styled.div`
  flex:1;background:${p=>p.theme.colors.background};display:flex;flex-direction:column;overflow:hidden;
`;
const ModalHeader = styled.div`
  display:flex;align-items:center;gap:12px;padding:16px 20px;
  border-bottom:1px solid ${p=>p.theme.colors.border};flex-shrink:0;
`;
const ModalTitle = styled.div`flex:1;font-size:16px;font-weight:700;color:${p=>p.theme.colors.text}`;
const ModalClose = styled.button`
  width:32px;height:32px;border-radius:8px;border:none;cursor:pointer;
  background:${p=>p.theme.mode==='dark'?'rgba(255,255,255,0.08)':'rgba(0,0,0,0.06)'};
  color:${p=>p.theme.colors.text};display:flex;align-items:center;justify-content:center;
`;
const ModalScroll= styled.div`flex:1;overflow:auto;padding:16px`;
const HeatDetCell= styled.div`
  width:52px;height:34px;border-radius:5px;
  background:${p=>p.$prob===0
    ? (p.theme.mode==='dark'?'rgba(255,255,255,0.05)':'rgba(0,0,0,0.05)')
    : `rgba(255,149,0,${0.15+p.$prob*0.82})`};
`;
const ModalLegend= styled.div`
  display:flex;align-items:center;gap:8px;padding:8px 16px;
  border-top:1px solid ${p=>p.theme.colors.border};font-size:10px;color:${p=>p.theme.colors.muted};
  background:${p=>p.theme.colors.surface};
`;
const LegendBar = styled.div`
  flex:1;height:12px;border-radius:4px;
  background:linear-gradient(to right, rgba(0,0,0,0.05) 0%, rgba(255,149,0,0.3) 30%, rgba(255,149,0,0.65) 65%, rgba(255,149,0,0.97) 100%);
`;

const UnavailCard = styled.div`
  display:flex;align-items:center;gap:8px;padding:16px 0;
  font-size:13px;color:${p=>p.theme.colors.muted};opacity:0.6;
`;

// ─── Count-up hook ────────────────────────────────────────────────────────────
function useCountUp(target, dur=800) {
  const [v, setV] = useState(0);
  const ref = useRef(target);
  useEffect(() => {
    ref.current = target;
    if (!target) { setV(0); return; }
    let id, t0=null, cancelled=false;
    const tick = (now) => {
      if (cancelled) return;
      if (!t0) t0=now;
      const p=Math.min((now-t0)/dur,1), e=1-Math.pow(1-p,3);
      setV(e*ref.current);
      if (p<1) id=requestAnimationFrame(tick); else setV(ref.current);
    };
    id = requestAnimationFrame(tick);
    return () => { cancelled=true; cancelAnimationFrame(id); };
  }, [target, dur]);
  return v;
}

// ─── Sub-components ───────────────────────────────────────────────────────────
function HeroCard({ label, rawValue, fmt, period, delay }) {
  const animated = useCountUp(rawValue??0);
  return (
    <Card $delay={delay}>
      <HeroBody>
        <HeroLabel>{label}</HeroLabel>
        <HeroValue>{fmt(animated)}</HeroValue>
        <HeroPeriod>{period}</HeroPeriod>
      </HeroBody>
    </Card>
  );
}

function MetricTile({ label, rawValue, fmt, value, caption, delay }) {
  const animated = useCountUp(rawValue??0);
  const display = rawValue!=null && fmt ? fmt(animated) : value;
  return (
    <Card $delay={delay}>
      <TileBody>
        <TileLabel>{label}</TileLabel>
        <TileValue>{display}</TileValue>
        {caption && <TileCap>{caption}</TileCap>}
      </TileBody>
    </Card>
  );
}

function DeliveryCard({ delivCount, pickCount, delay }) {
  const total = delivCount+pickCount;
  const delivPct = total>0 ? Math.round((delivCount/total)*100) : 50;
  const pickPct  = 100-delivPct;
  return (
    <Card $delay={delay}>
      <CardHeader>
        <IconBox><ArrowLeftRight size={14}/></IconBox>
        <CardTitle>Bezorging vs Afhalen</CardTitle>
      </CardHeader>
      <Divider/>
      <SplitRow>
        <SplitHalf>
          <TileLabel>Bezorging</TileLabel>
          <TileValue $large>{delivCount}</TileValue>
          <TileCap>{delivPct}%</TileCap>
        </SplitHalf>
        <VDivider/>
        <SplitHalf>
          <TileLabel>Afhalen</TileLabel>
          <TileValue $large style={{opacity:0.55}}>{pickCount}</TileValue>
          <TileCap>{pickPct}%</TileCap>
        </SplitHalf>
      </SplitRow>
      <ProgTrack>
        <ProgFill $pct={delivPct}/>
        <ProgRest/>
      </ProgTrack>
      <FootNote>Handig voor personeelsplanning bezorging</FootNote>
    </Card>
  );
}

function UpsellCard({ itemCount, orderCount, totalOrders, delay }) {
  const pct = totalOrders>0 ? ((orderCount/totalOrders)*100).toFixed(1) : '0.0';
  return (
    <Card $delay={delay}>
      <CardHeader>
        <IconBox><Sparkles size={14}/></IconBox>
        <CardTitle>Upsell</CardTitle>
      </CardHeader>
      <Divider/>
      <SplitRow>
        <SplitHalf>
          <TileLabel>Extra items</TileLabel>
          <TileValue $large>{itemCount}</TileValue>
          <TileCap>verkocht via upsell</TileCap>
        </SplitHalf>
        <VDivider/>
        <SplitHalf>
          <TileLabel>Orders met upsell</TileLabel>
          <TileValue $large>{pct}%</TileValue>
          <TileCap>{orderCount} van {totalOrders}</TileCap>
        </SplitHalf>
      </SplitRow>
    </Card>
  );
}

function SkeletonGeneral() {
  return (
    <Stack>
      <Card><HeroBody><Skel $h={13} style={{width:60,marginBottom:10}}/><Skel $h={50} style={{width:'55%',marginBottom:8}}/><Skel $h={12} style={{width:100}}/></HeroBody></Card>
      <TileRow>
        <Card><TileBody><Skel $h={12} style={{width:70,marginBottom:12}}/><Skel $h={32} style={{width:'60%'}}/></TileBody></Card>
        <Card><TileBody><Skel $h={12} style={{width:70,marginBottom:12}}/><Skel $h={32} style={{width:'60%'}}/></TileBody></Card>
      </TileRow>
      <Card><div style={{padding:18}}><Skel $h={80}/></div></Card>
    </Stack>
  );
}

// ─── Tab 1: Overzicht ─────────────────────────────────────────────────────────
function OverzichtTab() {
  const [loading, setLoading] = useState(true);
  const [period, setPeriod]           = useState('day');
  const [selectedDay, setSelectedDay] = useState(()=>fmtDateKey(new Date()));
  const [weekNum, setWeekNum]         = useState(()=>getISOWeek(new Date()));
  const [weekYear, setWeekYear]       = useState(()=>getISOWeekYear(new Date()));
  const [monthNum, setMonthNum]       = useState(()=>new Date().getMonth());
  const [monthYear, setMonthYear]     = useState(()=>new Date().getFullYear());
  const [customStart, setCustomStart] = useState(()=>fmtDateKey(new Date(Date.now()-6*86400000)));
  const [customEnd, setCustomEnd]     = useState(()=>fmtDateKey(new Date()));
  const [data, setData] = useState(null);
  const { hasPermission } = usePermissions();
  const canRevenue = hasPermission('can_view_revenue_statistics');

  const { startDate, endDate } = (() => {
    const today = fmtDateKey(new Date());
    if (period==='day')     return { startDate: selectedDay, endDate: selectedDay };
    if (period==='allTime') return { startDate: null, endDate: null };
    if (period==='custom')  return { startDate: customStart, endDate: customEnd };
    if (period==='week') {
      const mon = getMondayOfWeek(weekNum, weekYear);
      const sun = new Date(mon); sun.setDate(mon.getDate()+6);
      return { startDate: fmtDateKey(mon), endDate: fmtDateKey(sun)>today?today:fmtDateKey(sun) };
    }
    if (period==='month') {
      const s = new Date(monthYear,monthNum,1), e = new Date(monthYear,monthNum+1,0);
      return { startDate: fmtDateKey(s), endDate: fmtDateKey(e)>today?today:fmtDateKey(e) };
    }
    return { startDate: today, endDate: today };
  })();

  const periodLabel = (() => {
    const fmt=(s)=>{ const d=new Date(`${s}T00:00:00`); return `${d.getDate()} ${NL_MONTHS[d.getMonth()]} ${d.getFullYear()}`; };
    if (period==='day') {
      if (selectedDay===fmtDateKey(new Date())) return 'Vandaag';
      return new Date(`${selectedDay}T00:00:00`).toLocaleDateString('nl-NL',{weekday:'long',day:'numeric',month:'long'});
    }
    if (period==='week') {
      const mon=getMondayOfWeek(weekNum,weekYear), sun=new Date(mon); sun.setDate(mon.getDate()+6);
      return `Week ${weekNum} · ${mon.getDate()} ${NL_MONTHS_S[mon.getMonth()]} – ${sun.getDate()} ${NL_MONTHS_S[sun.getMonth()]}`;
    }
    if (period==='month')   return `${NL_MONTHS[monthNum]} ${monthYear}`;
    if (period==='allTime') return 'Alle tijd';
    if (startDate&&endDate) return `${fmt(startDate)} – ${fmt(endDate)}`;
    return 'Aangepast';
  })();

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await fetchGeneralStats(startDate, endDate)); }
    catch { setData(null); }
    setLoading(false);
  }, [startDate, endDate]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const client = getKmfcClient();
    if (!client) return;
    const sub = client.channel('stats-rt').on('postgres_changes',{event:'*',schema:'public',table:'orders'},load).subscribe();
    return () => sub.unsubscribe();
  }, [load]);

  const dayNav = (() => {
    const today=fmtDateKey(new Date());
    const goBack=()=>{const d=new Date(`${selectedDay}T00:00:00`);d.setDate(d.getDate()-1);const k=fmtDateKey(d);if(k>=MIN_DATE)setSelectedDay(k);};
    const goFwd =()=>{const d=new Date(`${selectedDay}T00:00:00`);d.setDate(d.getDate()+1);const k=fmtDateKey(d);if(k<=today)setSelectedDay(k);};
    return {goBack,goFwd,atMin:selectedDay<=MIN_DATE,atMax:selectedDay>=today};
  })();
  const weekNav = (() => {
    const today=new Date(),maxW=getISOWeek(today),maxY=getISOWeekYear(today);
    const goBack=()=>{let w=weekNum-1,y=weekYear;if(w<1){y--;w=52;}if(y<2025||(y===2025&&w<44))return;setWeekNum(w);setWeekYear(y);};
    const goFwd =()=>{let w=weekNum+1,y=weekYear;if(w>52){y++;w=1;}if(y>maxY||(y===maxY&&w>maxW))return;setWeekNum(w);setWeekYear(y);};
    const mon=getMondayOfWeek(weekNum,weekYear),sun=new Date(mon);sun.setDate(mon.getDate()+6);
    return {goBack,goFwd,atMin:weekYear<=2025&&weekNum<=44,atMax:weekYear>=maxY&&weekNum>=maxW,label:`Week ${weekNum} · ${weekYear}`,sub:`${mon.getDate()} ${NL_MONTHS_S[mon.getMonth()]} – ${sun.getDate()} ${NL_MONTHS_S[sun.getMonth()]}`};
  })();
  const monthNav = (() => {
    const today=new Date(),maxM=today.getMonth(),maxY=today.getFullYear();
    const goBack=()=>{let m=monthNum-1,y=monthYear;if(m<0){y--;m=11;}if(y<2025||(y===2025&&m<10))return;setMonthNum(m);setMonthYear(y);};
    const goFwd =()=>{let m=monthNum+1,y=monthYear;if(m>11){y++;m=0;}if(y>maxY||(y===maxY&&m>maxM))return;setMonthNum(m);setMonthYear(y);};
    return {goBack,goFwd,atMin:monthYear<=2025&&monthNum<=10,atMax:monthYear>=maxY&&monthNum>=maxM};
  })();

  const avgOrder = data && data.orderCount>0 ? data.totalRevCents/100/data.orderCount : 0;

  return (
    <>
      <PeriodBar>
        {[['day','Dag'],['week','Week'],['month','Maand'],['allTime','Altijd'],['custom','Aangepast']].map(([k,l])=>(
          <PeriodBtn key={k} $active={period===k} onClick={()=>setPeriod(k)}>{l}</PeriodBtn>
        ))}
      </PeriodBar>

      {period==='day' && (
        <Navigator>
          <NavBtn onClick={dayNav.goBack} disabled={dayNav.atMin}><ChevronLeft size={16}/></NavBtn>
          <NavLabel>{periodLabel}</NavLabel>
          <NavBtn onClick={dayNav.goFwd} disabled={dayNav.atMax}><ChevronRight size={16}/></NavBtn>
        </Navigator>
      )}
      {period==='week' && (
        <Navigator>
          <NavBtn onClick={weekNav.goBack} disabled={weekNav.atMin}><ChevronLeft size={16}/></NavBtn>
          <div style={{flex:1,textAlign:'center'}}>
            <NavLabel>{weekNav.label}</NavLabel>
            <NavSub>{weekNav.sub}</NavSub>
          </div>
          <NavBtn onClick={weekNav.goFwd} disabled={weekNav.atMax}><ChevronRight size={16}/></NavBtn>
        </Navigator>
      )}
      {period==='month' && (
        <Navigator>
          <NavBtn onClick={monthNav.goBack} disabled={monthNav.atMin}><ChevronLeft size={16}/></NavBtn>
          <NavLabel>{periodLabel}</NavLabel>
          <NavBtn onClick={monthNav.goFwd} disabled={monthNav.atMax}><ChevronRight size={16}/></NavBtn>
        </Navigator>
      )}
      {period==='custom' && (
        <Navigator style={{padding:'10px 14px',gap:8}}>
          <div style={{flex:1,display:'flex',flexDirection:'column',gap:4}}>
            <div style={{fontSize:10,fontWeight:700,letterSpacing:'0.08em',textTransform:'uppercase',opacity:0.5}}>Van</div>
            <DatePickerField value={customStart} onChange={setCustomStart}/>
          </div>
          <div style={{width:1,background:'var(--border)',alignSelf:'stretch',margin:'4px 0'}}/>
          <div style={{flex:1,display:'flex',flexDirection:'column',gap:4}}>
            <div style={{fontSize:10,fontWeight:700,letterSpacing:'0.08em',textTransform:'uppercase',opacity:0.5}}>Tot en met</div>
            <DatePickerField value={customEnd} onChange={setCustomEnd}/>
          </div>
        </Navigator>
      )}

      {loading ? <SkeletonGeneral/> : (
        <Stack>
          {canRevenue && (
            <HeroCard label="OMZET" rawValue={data?data.totalRevCents/100:0} fmt={fmtEuro} period={periodLabel} delay={0}/>
          )}
          <TileRow>
            <MetricTile label="Bestellingen" rawValue={data?.orderCount??0} fmt={fmtNum} caption="in periode" delay={50}/>
            {canRevenue && (
              <MetricTile label="Gem. waarde" rawValue={avgOrder} fmt={fmtEuro} caption="per bestelling" delay={100}/>
            )}
          </TileRow>
          <DeliveryCard delivCount={data?.delivCount??0} pickCount={data?.pickCount??0} delay={150}/>

          {canRevenue && (
            <TileRow>
              <MetricTile label="Bezorgkosten" rawValue={data?data.delivFeeCents/100:0} fmt={fmtEuro} caption="in periode" delay={200}/>
              <MetricTile label="Totale afstand" value={data?.totalKm>0?`${data.totalKm.toFixed(1)} km`:'—'} caption="in periode" delay={250}/>
              <MetricTile label="Fooi" rawValue={data?data.totalTipCents/100:0} fmt={fmtEuro} caption="in periode" delay={300}/>
            </TileRow>
          )}

          <UpsellCard itemCount={data?.upsellItemCount??0} orderCount={data?.upsellOrderCount??0} totalOrders={data?.orderCount??0} delay={canRevenue?300:200}/>
        </Stack>
      )}
    </>
  );
}

// ─── Tab 2: Piekuren ──────────────────────────────────────────────────────────
function PiekurenTab() {
  const [state, setState] = useState('idle');
  const [data,  setData]  = useState(null);
  const [showDetail,      setShowDetail]      = useState(false);
  const [showProbInfo,    setShowProbInfo]     = useState(false);

  useEffect(() => {
    let cancelled = false;
    setState('loading');
    fetchTrendsData()
      .then(d  => { if(!cancelled){ setData(d); setState('loaded'); }})
      .catch(() => { if(!cancelled) setState('failed'); });
    return () => { cancelled=true; };
  }, []);

  const loading = state==='idle'||state==='loading';
  const failed  = state==='failed';

  const maxHeat = data ? Math.max(1,...Object.values(data.hourlyGrid)) : 1;
  const maxAvg  = data ? Math.max(1,...data.avgOrdersPerWeekday) : 1;

  return (
    <Stack>
      {/* Piekuren heatmap */}
      <Card $delay={0}>
        <CardHeader>
          <IconBox><Clock size={14}/></IconBox>
          <div style={{flex:1}}>
            <CardTitle>Piekuren per dag</CardTitle>
            <CardSub>gebaseerd op {TRENDS_WEEKS_BACK} weken · feestdagen uitgesloten</CardSub>
          </div>
          {loading && <div style={{width:16,height:16,borderRadius:'50%',border:'2px solid rgba(128,128,128,0.3)',borderTopColor:'transparent',animation:'spin 0.8s linear infinite'}}/>}
        </CardHeader>
        <Divider/>
        <div style={{padding:'14px 16px 16px'}}>
          {loading && <Skel $h={160}/>}
          {failed && <UnavailCard>× Kon data niet laden</UnavailCard>}
          {state==='loaded' && (!data?.visibleHours?.length
            ? <UnavailCard>× Onvoldoende data beschikbaar</UnavailCard>
            : (
              <>
                <HeatGrid>
                  <HeatRow>
                    <HeatLabel/>
                    {DAY_SHORT.map(d=><DayLabel key={d}>{d}</DayLabel>)}
                  </HeatRow>
                  {data.visibleHours.map(h=>(
                    <HeatRow key={h}>
                      <HeatLabel>{h}u</HeatLabel>
                      {Array.from({length:7},(_,d)=>{
                        const count=data.hourlyGrid[`${d}_${h}`]||0;
                        const intensity=maxHeat>0?count/maxHeat:0;
                        return <HeatCell key={d} $intensity={intensity}/>;
                      })}
                    </HeatRow>
                  ))}
                </HeatGrid>
                <div style={{display:'flex',justifyContent:'flex-end',marginTop:10}}>
                  <button onClick={()=>setShowDetail(true)} style={{
                    display:'flex',alignItems:'center',gap:4,padding:'5px 10px',
                    borderRadius:20,border:'none',cursor:'pointer',fontFamily:'inherit',
                    fontSize:11,fontWeight:600,background:'rgba(128,128,128,0.1)',color:'inherit',opacity:0.6
                  }}>
                    <Maximize2 size={10}/> Vergroot
                  </button>
                </div>
              </>
            )
          )}
        </div>
      </Card>

      {/* Piekdagen */}
      <Card $delay={50}>
        <CardHeader>
          <IconBox><BarChart2 size={14}/></IconBox>
          <div style={{flex:1}}>
            <CardTitle>Piekdagen van de week</CardTitle>
            <CardSub>gem. orders per dag · feestdagen uitgesloten</CardSub>
          </div>
        </CardHeader>
        <Divider/>
        <div style={{padding:'14px 16px 16px'}}>
          {loading && <Skel $h={140}/>}
          {failed && <UnavailCard>× Onvoldoende data beschikbaar</UnavailCard>}
          {state==='loaded' && (!data?.avgOrdersPerWeekday?.length
            ? <UnavailCard>× Onvoldoende data beschikbaar</UnavailCard>
            : (
              <Stack $gap={7}>
                {DAY_FULL.map((name,d)=>{
                  const avg  = data.avgOrdersPerWeekday[d];
                  const closed = data.closedDays.has(d);
                  return (
                    <div key={d} style={{display:'flex',alignItems:'center',gap:10}}>
                      <div style={{width:88,fontSize:13,fontWeight:500,opacity:closed?0.3:1,flexShrink:0}}>{name}</div>
                      {closed
                        ? <div style={{fontSize:11,opacity:0.25,flex:1}}>Gesloten</div>
                        : <><BarTrack><BarFill $pct={maxAvg>0?(avg/maxAvg)*100:0}/></BarTrack>
                          <div style={{fontSize:12,fontWeight:600,width:32,textAlign:'right',opacity:0.6,flexShrink:0}}>{avg.toFixed(1)}</div></>
                      }
                    </div>
                  );
                })}
              </Stack>
            )
          )}
        </div>
      </Card>

      {/* Order probability */}
      <Card $delay={100}>
        <CardHeader>
          <IconBox><Sparkles size={14}/></IconBox>
          <CardTitle>Vandaag nog een order?</CardTitle>
        </CardHeader>
        <Divider/>
        <div style={{padding:'14px 16px 16px'}}>
          {loading && <Skel $h={80}/>}
          {failed && <UnavailCard>× Kon data niet laden</UnavailCard>}
          {state==='loaded' && (
            data?.todayOrderProbability!=null ? (
              <>
                <div>
                  <ProbValue>{Math.round(data.todayOrderProbability*100)}%</ProbValue>
                  <ProbSub>kans op nog een order</ProbSub>
                </div>
                <ProbTrack>
                  <ProbFill $val={data.todayOrderProbability} $pct={data.todayOrderProbability*100}/>
                </ProbTrack>
                <Divider style={{margin:'2px 0 10px'}}/>
                <InfoBtn onClick={()=>setShowProbInfo(v=>!v)}>
                  <Info size={12} style={{opacity:0.4,flexShrink:0}}/>
                  <InfoText>Hoe wordt dit berekend?</InfoText>
                  <span style={{marginLeft:'auto',opacity:0.4}}>{showProbInfo?<ChevronLeft size={12} style={{transform:'rotate(90deg)'}}/>:<ChevronLeft size={12} style={{transform:'rotate(-90deg)'}}/>}</span>
                </InfoBtn>
                {showProbInfo && (
                  <InfoExpand>
                    <div style={{display:'flex',alignItems:'flex-start',gap:6,marginTop:8,marginBottom:6}}>
                      <InfoNr>1</InfoNr>
                      <span>Basis (λ): som van het historisch gemiddelde orders per resterend uur van vandaag ({data.probWeekdayName}, na {data.probCurrentHour}:00), op basis van {TRENDS_WEEKS_BACK} weken.</span>
                    </div>
                    <div style={{display:'flex',alignItems:'flex-start',gap:6,marginBottom:6}}>
                      <InfoNr>2</InfoNr>
                      <span>Drukte vandaag: er zijn al {data.ordersTodayCount} orders binnen. De gedempte correctiefactor is {data.probDampedFactor}× — demping voorkomt explosie bij uitschieters.</span>
                    </div>
                    <div style={{display:'flex',alignItems:'flex-start',gap:6,marginBottom:6}}>
                      <InfoNr>3</InfoNr>
                      <span>Verwacht nog ~{data.probExpectedRemaining} order(s). Poisson: P = 1 − e^(−λ) = {Math.round(data.todayOrderProbability*100)}%.</span>
                    </div>
                    {!data.probIsReliable && (
                      <div style={{display:'flex',alignItems:'flex-start',gap:6,marginBottom:6}}>
                        <InfoNr>⚠</InfoNr>
                        <span>Minder dan 8 weken data voor deze weekdag — schatting is minder betrouwbaar.</span>
                      </div>
                    )}
                    {data.probExtreme && (
                      <div style={{display:'flex',alignItems:'flex-start',gap:6}}>
                        <InfoNr>⚠</InfoNr>
                        <span>Vandaag is uitzonderlijk druk (factor &gt;2×). De correctie is begrensd op 2,5× om overdrijving te voorkomen.</span>
                      </div>
                    )}
                  </InfoExpand>
                )}
              </>
            ) : (
              <UnavailCard>× We zijn gesloten of er is onvoldoende historische data</UnavailCard>
            )
          )}
        </div>
      </Card>

      {/* Heatmap detail modal */}
      {showDetail && data && (
        <HeatmapModal data={data} maxVal={maxHeat} weeksBack={TRENDS_WEEKS_BACK} onClose={()=>setShowDetail(false)}/>
      )}

      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </Stack>
  );
}

function HeatmapModal({ data, weeksBack, onClose }) {
  const prob  = (count) => weeksBack>0 ? Math.min(1.0, count/weeksBack) : 0;
  return (
    <ModalOverlay onClick={(e)=>{ if(e.target===e.currentTarget)onClose(); }}>
      <ModalInner>
        <ModalHeader>
          <ModalTitle>Piekuren per dag</ModalTitle>
          <ModalClose onClick={onClose}><X size={14}/></ModalClose>
        </ModalHeader>
        <ModalScroll>
          <div style={{display:'inline-block',minWidth:'100%'}}>
            {/* Header row */}
            <div style={{display:'flex',gap:0,marginBottom:0}}>
              <div style={{width:42,flexShrink:0}}/>
              {DAY_SHORT.map(d=>(
                <div key={d} style={{width:52,textAlign:'center',fontSize:11,fontWeight:700,opacity:0.55,paddingBottom:6,flexShrink:0}}>{d}</div>
              ))}
            </div>
            <div style={{height:1,background:'rgba(128,128,128,0.15)',marginBottom:4}}/>
            {data.visibleHours.map(h=>(
              <div key={h} style={{display:'flex',gap:0,alignItems:'center',marginBottom:2}}>
                <div style={{width:42,fontSize:10,opacity:0.4,textAlign:'right',paddingRight:6,flexShrink:0}}>{h}:00</div>
                {Array.from({length:7},(_,d)=>{
                  const count=data.hourlyGrid[`${d}_${h}`]||0;
                  return <HeatDetCell key={d} $prob={prob(count)}/>;
                })}
              </div>
            ))}
          </div>
        </ModalScroll>
        <ModalLegend>
          <span>Weinig</span>
          <LegendBar style={{flex:1,height:12,borderRadius:4}}/>
          <span>Veel</span>
          <span style={{opacity:0.6}}>· feestdagen niet meegeteld</span>
        </ModalLegend>
      </ModalInner>
    </ModalOverlay>
  );
}

// ─── Tab 3: Producten ─────────────────────────────────────────────────────────
function ProductenTab() {
  const [state, setState] = useState('idle');
  const [data,  setData]  = useState(null);

  useEffect(() => {
    let cancelled = false;
    setState('loading');
    fetchProductsData()
      .then(d  => { if(!cancelled){ setData(d); setState('loaded'); }})
      .catch(() => { if(!cancelled) setState('failed'); });
    return () => { cancelled=true; };
  }, []);

  const loading = state==='idle'||state==='loading';
  const failed  = state==='failed';
  const trends  = data?.productTrends || [];
  const ranked  = trends.filter(t=>t.recentQty>=1&&!t.name.toLowerCase().includes('test'));
  const maxWeekly = ranked[0]?.weeklyAvg || 1;
  const stijgers  = trends.filter(t=>t.isTrending  && !t.name.toLowerCase().includes('test')).sort((a,b)=>b.changeRatio-a.changeRatio);
  const dalers    = trends.filter(t=>t.isDeclining && !t.name.toLowerCase().includes('test')).sort((a,b)=>a.changeRatio-b.changeRatio);

  return (
    <Stack>
      {/* Product populariteit */}
      <Card $delay={0}>
        <CardHeader>
          <IconBox><ShoppingCart size={14}/></IconBox>
          <div style={{flex:1}}>
            <CardTitle>Product populariteit</CardTitle>
            <CardSub>gem. per week · afgelopen 4 weken</CardSub>
          </div>
          {loading && <div style={{width:16,height:16,borderRadius:'50%',border:'2px solid rgba(128,128,128,0.3)',borderTopColor:'transparent',animation:'spin 0.8s linear infinite'}}/>}
        </CardHeader>
        <Divider/>
        {loading && <div style={{padding:16}}><Skel $h={200}/></div>}
        {failed  && <div style={{padding:16}}><UnavailCard>× Onvoldoende productdata</UnavailCard></div>}
        {state==='loaded' && (
          ranked.length===0
            ? <div style={{padding:16}}><UnavailCard>× Onvoldoende productdata</UnavailCard></div>
            : ranked.map((t,i)=>(
              <React.Fragment key={t.key}>
                <ProdRow>
                  <div style={{flex:1}}>
                    <ProdName>{t.name}</ProdName>
                    <ProdBar><ProdBarFill $pct={maxWeekly>0?(t.weeklyAvg/maxWeekly)*100:0}/></ProdBar>
                  </div>
                  <ProdWeekly>
                    <ProdWNum>{t.weeklyAvg.toFixed(1)}<ProdWUnit>/wk</ProdWUnit></ProdWNum>
                    {t.previousQty>0 && (
                      <ProdDelta $up={t.changeRatio>0}>
                        {t.changeRatio>0?<TrendingUp size={8}/>:<TrendingDown size={8}/>}
                        {Math.abs(Math.round(t.changeRatio*100))}%
                      </ProdDelta>
                    )}
                  </ProdWeekly>
                </ProdRow>
                {i<ranked.length-1 && <Divider style={{margin:'0 16px'}}/>}
              </React.Fragment>
            ))
        )}
      </Card>

      {/* Stijgers & Dalers */}
      {state==='loaded' && (stijgers.length>0||dalers.length>0) && (
        <Card $delay={50}>
          <CardHeader>
            <IconBox><BarChart2 size={14}/></IconBox>
            <div style={{flex:1}}>
              <CardTitle>Trending vs. Dalend</CardTitle>
              <CardSub>vergeleken met 4 weken ervoor</CardSub>
            </div>
          </CardHeader>
          <Divider/>
          {stijgers.length>0 && (
            <TrendSection items={stijgers} isRising={true}/>
          )}
          {stijgers.length>0&&dalers.length>0 && <Divider style={{margin:'0 16px'}}/>}
          {dalers.length>0 && (
            <TrendSection items={dalers} isRising={false}/>
          )}
        </Card>
      )}

      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </Stack>
  );
}

function TrendSection({ items, isRising }) {
  return (
    <div>
      <div style={{display:'flex',alignItems:'center',gap:6,padding:'12px 16px 6px'}}>
        {isRising
          ? <TrendingUp size={13} style={{color:'rgba(52,199,89,0.85)'}}/>
          : <TrendingDown size={13} style={{color:'rgba(255,59,48,0.75)'}}/>
        }
        <span style={{fontSize:12,fontWeight:700,opacity:0.6}}>{isRising?'Stijgers':'Dalers'}</span>
      </div>
      {items.slice(0,5).map((t,i)=>(
        <div key={t.key} style={{display:'flex',alignItems:'center',gap:12,padding:'8px 16px'}}>
          <span style={{flex:1,fontSize:13,fontWeight:500}}>{t.name}</span>
          <TrendBadge $up={isRising}>
            {isRising?<TrendingUp size={9}/>:<TrendingDown size={9}/>}
            {Math.round(Math.abs(t.changeRatio)*100)}%
          </TrendBadge>
          <div style={{textAlign:'right',minWidth:44}}>
            <div style={{fontSize:12,fontWeight:600}}>{t.recentQty}×</div>
            <div style={{fontSize:10,opacity:0.4}}>was {t.previousQty}×</div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
const TABS = [
  { key:'overzicht', label:'Overzicht', Icon:BarChart2 },
  { key:'piekuren',  label:'Piekuren',  Icon:Clock },
  { key:'producten', label:'Producten', Icon:ShoppingCart },
];

export default function StatsScreen() {
  const [activeTab, setActiveTab] = useState('overzicht');
  return (
    <Container>
      <Content>
        <Inner>
          <PageTitle>Statistieken</PageTitle>
          <TabBar>
            {TABS.map(({key,label,Icon})=>(
              <TabBtn key={key} $active={activeTab===key} onClick={()=>setActiveTab(key)}>
                <Icon size={11}/>{label}
              </TabBtn>
            ))}
          </TabBar>
          {activeTab==='overzicht'  && <OverzichtTab/>}
          {activeTab==='piekuren'   && <PiekurenTab/>}
          {activeTab==='producten'  && <ProductenTab/>}
        </Inner>
      </Content>
    </Container>
  );
}
